import { VOICEOVER_VIDEO_STEP_ORDER } from '@/features/assistant/constants'
import type {
  AssistantPlan,
  AssistantPlannerSource,
  AssistantStep,
  AssistantStepType,
} from '@/features/assistant/types'
import type { MediaMetadata } from '@/types/storage'
import type { MossTtsVoice } from '@/features/editor/services/moss-tts-service'
import type { VoiceoverVideoPlan } from './planner-schema'

export type AssistantBgmMode = 'generate' | 'selected' | 'none'

export interface VoiceoverPlanningProjectSignals {
  existingVisualMediaCount?: number
  usedTimelineVisualMediaCount?: number
  transcriptReadyMediaCount?: number
  currentTimelineItemCount?: number
  currentTimelineDurationSeconds?: number
}

export interface VoiceoverEditingStrategy {
  visualSource: 'reuse-existing' | 'selected-reference' | 'generate-reference' | 'mixed'
  narrativeStructure: 'voiceover-led' | 'visual-led' | 'mixed'
  shouldGenerateVoiceover: boolean
  shouldGenerateCaptions: boolean
  shouldGenerateBgm: boolean
  shouldExportDraft: boolean
}

export interface VoiceoverVisualPlan {
  primaryApproach: 'reuse-timeline-visuals' | 'use-selected-references' | 'generate-reference-images'
  reuseExistingTimelineVisuals: boolean
  selectedReferenceCount: number
  generatedReferenceTargetCount: number
  recommendedPacing: 'slow' | 'medium' | 'fast'
}

export interface VoiceoverCaptionPlan {
  source: 'generated-voiceover-transcript'
  recommendedStyle: 'subtitle-default' | 'subtitle-highlight'
  transcriptReadyMediaCount: number
}

export interface VoiceoverBgmPlan {
  mode: AssistantBgmMode
  source: 'generate' | 'selected' | 'none'
  selectedMediaLabel?: string | null
  recommendedTone: VoiceoverVideoPlan['tone']
}

export interface VoiceoverTimelineStrategy {
  mode: 'reuse-current-timeline' | 'create-draft-timeline'
  preserveCurrentTimeline: boolean
  reason:
    | 'explicit-user-request'
    | 'protect-existing-edit'
    | 'current-timeline-reuse'
    | 'default-current-project'
}

export interface VoiceoverAssistantPreferences {
  ttsVoice?: MossTtsVoice
  bgmMode?: AssistantBgmMode
  selectedBgmMediaId?: string | null
  selectedBgmMediaLabel?: string | null
  selectedReferenceMediaIds?: string[]
  selectedReferenceMediaLabels?: string[]
  projectSignals?: VoiceoverPlanningProjectSignals
  timelineStrategy?: VoiceoverTimelineStrategy
  plannerSource?: AssistantPlannerSource
}

interface VoiceoverVideoAssistantContext extends VoiceoverVideoPlan, VoiceoverAssistantPreferences {
  projectId?: string
  mediaProjectId?: string
  projectName: string
  sourceProjectId?: string
  sourceProjectName?: string
  executionProjectId?: string
  executionProjectName?: string
  executionProjectWasDuplicated?: boolean
  width?: number
  height?: number
  fps?: number
  generatedReferenceImageCount: number
  generatedReferenceImageFiles?: File[]
  generatedReferenceImageDimensions?: Array<{ width: number; height: number }>
  generatedReferenceMediaIds: string[]
  generatedReferenceMediaLabels: string[]
  insertedReferenceItemIds?: string[]
  insertedReferenceItemCount?: number
  insertedReferenceTransitionCount?: number
  generatedAudioFile?: File
  generatedAudioBlobUrl?: string
  generatedAudioDuration?: number
  generatedAudioMediaId?: string
  generatedAudioMedia?: MediaMetadata
  generatedAudioItemId?: string
  insertedCaptionCount?: number
  generatedBgmFile?: File
  generatedBgmBlobUrl?: string
  generatedBgmDuration?: number
  generatedBgmPrompt?: string | null
  generatedBgmModel?: string
  generatedBgmMediaId?: string
  generatedBgmMedia?: MediaMetadata
  generatedBgmCoverageDuration?: number
  generatedBgmItemId?: string
  generatedBgmItemCount?: number
  exportFileName?: string
  exportMimeType?: string
  exportFileSize?: number
  transcriptSegments?: Array<{ text: string; start: number; end: number }>
  editingStrategy?: VoiceoverEditingStrategy
  visualPlan?: VoiceoverVisualPlan
  captionPlan?: VoiceoverCaptionPlan
  bgmPlan?: VoiceoverBgmPlan
  timelineStrategy?: VoiceoverTimelineStrategy
}

const ASSISTANT_GENERATED_REFERENCE_IMAGES_ENABLED = false

function resolveGeneratedReferenceImageCount(
  durationSeconds: number,
  selectedReferenceMediaIds: string[] | undefined,
  projectSignals?: VoiceoverPlanningProjectSignals,
): number {
  if (!ASSISTANT_GENERATED_REFERENCE_IMAGES_ENABLED) {
    return 0
  }

  if ((selectedReferenceMediaIds?.length ?? 0) > 0) {
    return 0
  }

  if ((projectSignals?.usedTimelineVisualMediaCount ?? 0) > 0) {
    return 0
  }

  if ((projectSignals?.existingVisualMediaCount ?? 0) > 0) {
    return 0
  }

  if (durationSeconds <= 20) {
    return 1
  }

  if (durationSeconds <= 45) {
    return 2
  }

  return 3
}

function createStep(
  type: AssistantStepType,
  title: string,
  payload?: Record<string, unknown>,
): AssistantStep {
  return {
    id: crypto.randomUUID(),
    type,
    title,
    status: 'pending',
    payload,
  }
}

function buildProjectName(topic: string): string {
  const trimmed = topic.trim()
  if (!trimmed) {
    return 'AI 口播草稿'
  }

  return trimmed.length > 24 ? `${trimmed.slice(0, 24)}...` : trimmed
}

function resolveVisualSource(params: {
  selectedReferenceCount: number
  generatedReferenceImageCount: number
  projectSignals?: VoiceoverPlanningProjectSignals
}): VoiceoverEditingStrategy['visualSource'] {
  const hasReusableTimelineVisuals = (params.projectSignals?.usedTimelineVisualMediaCount ?? 0) > 0
  const hasSelectedReferences = params.selectedReferenceCount > 0
  const hasGeneratedReferences = params.generatedReferenceImageCount > 0

  if ((hasReusableTimelineVisuals && hasSelectedReferences) || (hasSelectedReferences && hasGeneratedReferences)) {
    return 'mixed'
  }
  if (hasReusableTimelineVisuals) {
    return 'reuse-existing'
  }
  if (hasSelectedReferences) {
    return 'selected-reference'
  }
  return 'generate-reference'
}

function buildVoiceoverEditingStrategy(params: {
  plannerOutput: VoiceoverVideoPlan
  bgmMode: AssistantBgmMode
  selectedReferenceCount: number
  generatedReferenceImageCount: number
  projectSignals?: VoiceoverPlanningProjectSignals
}): VoiceoverEditingStrategy {
  return {
    visualSource: resolveVisualSource(params),
    narrativeStructure:
      (params.projectSignals?.usedTimelineVisualMediaCount ?? 0) > 0 ? 'mixed' : 'voiceover-led',
    shouldGenerateVoiceover: params.plannerOutput.needsTts,
    shouldGenerateCaptions: params.plannerOutput.needsCaptions,
    shouldGenerateBgm: params.bgmMode !== 'none' && params.plannerOutput.needsBgm,
    shouldExportDraft: params.plannerOutput.exportDraft,
  }
}

function buildVoiceoverVisualPlan(params: {
  selectedReferenceCount: number
  generatedReferenceImageCount: number
  durationSeconds: number
  projectSignals?: VoiceoverPlanningProjectSignals
}): VoiceoverVisualPlan {
  const reuseExistingTimelineVisuals = (params.projectSignals?.usedTimelineVisualMediaCount ?? 0) > 0
  return {
    primaryApproach: reuseExistingTimelineVisuals
      ? 'reuse-timeline-visuals'
      : params.selectedReferenceCount > 0
        ? 'use-selected-references'
        : 'generate-reference-images',
    reuseExistingTimelineVisuals,
    selectedReferenceCount: params.selectedReferenceCount,
    generatedReferenceTargetCount: params.generatedReferenceImageCount,
    recommendedPacing:
      params.durationSeconds <= 20 ? 'fast' : params.durationSeconds <= 45 ? 'medium' : 'slow',
  }
}

function buildVoiceoverCaptionPlan(
  transcriptReadyMediaCount: number,
): VoiceoverCaptionPlan {
  return {
    source: 'generated-voiceover-transcript',
    recommendedStyle: transcriptReadyMediaCount > 0 ? 'subtitle-highlight' : 'subtitle-default',
    transcriptReadyMediaCount,
  }
}

function buildVoiceoverBgmPlan(params: {
  bgmMode: AssistantBgmMode
  selectedBgmMediaLabel?: string | null
  tone: VoiceoverVideoPlan['tone']
}): VoiceoverBgmPlan {
  return {
    mode: params.bgmMode,
    source: params.bgmMode === 'selected' ? 'selected' : params.bgmMode === 'generate' ? 'generate' : 'none',
    selectedMediaLabel: params.selectedBgmMediaLabel ?? null,
    recommendedTone: params.tone,
  }
}

function buildVoiceoverTimelineStrategy(
  strategy: VoiceoverTimelineStrategy | undefined,
): VoiceoverTimelineStrategy {
  return (
    strategy ?? {
      mode: 'reuse-current-timeline',
      preserveCurrentTimeline: false,
      reason: 'default-current-project',
    }
  )
}

export function createVoiceoverVideoAssistantPlan(
  sourcePrompt: string,
  plannerOutput: VoiceoverVideoPlan,
  preferences: VoiceoverAssistantPreferences = {},
): AssistantPlan<VoiceoverVideoAssistantContext> {
  const bgmMode = preferences.bgmMode ?? (plannerOutput.needsBgm ? 'generate' : 'none')
  const selectedReferenceCount = preferences.selectedReferenceMediaIds?.length ?? 0
  const includeBgmSteps = bgmMode !== 'none' && (plannerOutput.needsBgm || bgmMode === 'selected')
  const generatedReferenceImageCount = resolveGeneratedReferenceImageCount(
    plannerOutput.durationSeconds,
    preferences.selectedReferenceMediaIds,
    preferences.projectSignals,
  )
  const timelineStrategy = buildVoiceoverTimelineStrategy(preferences.timelineStrategy)
  const editingStrategy = buildVoiceoverEditingStrategy({
    plannerOutput,
    bgmMode,
    selectedReferenceCount,
    generatedReferenceImageCount,
    projectSignals: preferences.projectSignals,
  })
  const visualPlan = buildVoiceoverVisualPlan({
    selectedReferenceCount,
    generatedReferenceImageCount,
    durationSeconds: plannerOutput.durationSeconds,
    projectSignals: preferences.projectSignals,
  })
  const captionPlan = buildVoiceoverCaptionPlan(
    preferences.projectSignals?.transcriptReadyMediaCount ?? 0,
  )
  const bgmPlan = buildVoiceoverBgmPlan({
    bgmMode,
    selectedBgmMediaLabel: preferences.selectedBgmMediaLabel,
    tone: plannerOutput.tone,
  })
  const includeGeneratedReferenceImageSteps = generatedReferenceImageCount > 0

  const stepsByType = new Map<AssistantStepType, AssistantStep>([
    [
      'get_current_project',
      createStep('get_current_project', '读取当前项目上下文', {
        aspectRatio: plannerOutput.aspectRatio,
        durationSeconds: plannerOutput.durationSeconds,
        requireExistingProject: true,
        referenceMediaCount: preferences.selectedReferenceMediaIds?.length ?? 0,
        timelineStrategyMode: timelineStrategy.mode,
        preserveCurrentTimeline: timelineStrategy.preserveCurrentTimeline,
      }),
    ],
    [
      'generate_reference_images',
      createStep('generate_reference_images', '生成参考图片', {
        targetCount: generatedReferenceImageCount,
      }),
    ],
    [
      'save_generated_images_to_media',
      createStep('save_generated_images_to_media', '保存参考图片到媒体库'),
    ],
    [
      'select_reference_media',
      createStep('select_reference_media', '选择参考素材', {
        referenceMediaCount: preferences.selectedReferenceMediaIds?.length ?? 0,
      }),
    ],
    [
      'prepare_materials',
      createStep('prepare_materials', '准备素材', {
        referenceMediaCount:
          (preferences.selectedReferenceMediaIds?.length ?? 0) + generatedReferenceImageCount,
        bgmMode,
        reuseExistingVisuals: (preferences.projectSignals?.existingVisualMediaCount ?? 0) > 0,
        timelineStrategyMode: timelineStrategy.mode,
      }),
    ],
    [
      'generate_tts_audio',
      createStep('generate_tts_audio', '生成 AI 配音', {
        topic: plannerOutput.topic,
        tone: plannerOutput.tone,
        voice: preferences.ttsVoice ?? 'Xiaoyu',
      }),
    ],
    [
      'save_generated_audio_to_media',
      createStep('save_generated_audio_to_media', '保存配音到媒体库'),
    ],
    ['insert_audio_to_timeline', createStep('insert_audio_to_timeline', '插入配音到时间线')],
    ['transcribe_media', createStep('transcribe_media', '生成配音字幕转录')],
    ['insert_transcript_as_captions', createStep('insert_transcript_as_captions', '插入字幕轨')],
    [
      'generate_bgm',
      createStep('generate_bgm', '生成背景音乐', {
        tone: plannerOutput.tone,
        mode: bgmMode,
        selectedMediaLabel: preferences.selectedBgmMediaLabel ?? undefined,
      }),
    ],
    ['attach_bgm_to_timeline', createStep('attach_bgm_to_timeline', '插入背景音乐到时间线')],
    ['start_export', createStep('start_export', '导出草稿视频')],
  ])

  const steps = VOICEOVER_VIDEO_STEP_ORDER.filter((type) => {
    if (type === 'generate_reference_images' || type === 'save_generated_images_to_media') {
      return includeGeneratedReferenceImageSteps
    }

    if (type === 'generate_bgm' || type === 'attach_bgm_to_timeline') {
      return includeBgmSteps
    }

    if (type === 'start_export') {
      return plannerOutput.exportDraft
    }

    if (
      type === 'generate_tts_audio' ||
      type === 'save_generated_audio_to_media' ||
      type === 'insert_audio_to_timeline'
    ) {
      return plannerOutput.needsTts
    }

    if (type === 'transcribe_media' || type === 'insert_transcript_as_captions') {
      return plannerOutput.needsCaptions
    }

    return true
  })
    .map((type) => stepsByType.get(type))
    .filter((step): step is AssistantStep => !!step)

  return {
    id: crypto.randomUUID(),
    taskType: plannerOutput.taskType,
    title: `口播视频草稿：${plannerOutput.topic}`,
    sourcePrompt,
    planner: preferences.plannerSource ?? 'deepseek',
    createdAt: Date.now(),
    context: {
      ...plannerOutput,
      needsBgm: includeBgmSteps,
      ttsVoice: preferences.ttsVoice ?? 'Xiaoyu',
      bgmMode,
      selectedBgmMediaId: preferences.selectedBgmMediaId ?? null,
      selectedBgmMediaLabel: preferences.selectedBgmMediaLabel ?? null,
      selectedReferenceMediaIds: preferences.selectedReferenceMediaIds ?? [],
      selectedReferenceMediaLabels: preferences.selectedReferenceMediaLabels ?? [],
      projectSignals: preferences.projectSignals,
      generatedReferenceImageCount,
      generatedReferenceMediaIds: [],
      generatedReferenceMediaLabels: [],
      editingStrategy,
      visualPlan,
      captionPlan,
      bgmPlan,
      timelineStrategy,
      projectName: buildProjectName(plannerOutput.topic),
    },
    steps,
  }
}

export type { VoiceoverVideoAssistantContext }
